import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import CheckBoxFormik from "../../../components/UI/Checkbox/CheckBoxFormik";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import HtmlEditor from "../../../components/UI/HtmlEditorInput/HtmlEditor";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import SelectTipoAcaoFiscalFormik from "../../../components/UI/SelectInput/SelectTipoAcaoFiscalFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import RadioButtonFormik from "../../../components/UI/RadioButton/RadioButtonFormik";

const validationSchema = Yup.object().shape({
  sequenceTextoPadrao: Yup.string().default(""),
  descricaoTipoAcaoFiscal: Yup.string().nullable(),
  textoPadrao: Yup.string().default("").required("Informe o Texto Padrao").min(1, "Nome muito curto"),
  sequenceTipoAcaoFiscal: Yup.string()
    .default("")
    .when("tipoDocumento", {
      is: (tipoDocumento) => tipoDocumento === "O",
      then: Yup.string().required("Informe tipo da Ação Fiscal"),
    }),
  tipoDocumento: Yup.string().default("").required("Informe o Tipo do Documento"),
  fgContabil: Yup.boolean().default(true),
});

const initialValues = {};

validationSchema._nodes.forEach((key) => {
  initialValues[key] = key === "fgContabil" ? false : "";
});

const FormTextoPadrao = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [dadosInfo, setDadosInfo] = useState({});

  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.sequenceTextoPadrao) requestForm(apiRoutes.fiscalizacao.parametrizacao.alteraTextoPadrao, values);
      else requestForm(apiRoutes.fiscalizacao.parametrizacao.incluiTextoPadrao, values);
    },
    [requestForm],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) formik.setSubmitting(false);

    if (errorForm && isValid && !isLoading) formik.setErrors({ ...formik.errors, ...errorForm });
  }, [formik, setSubmitting, setErrors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  useEffect(() => {
    if (!dataForm && !errorForm) return;

    if (dataForm) {
      toast.success("Texto Padrao salvo com sucesso");
      history.push("/app/textopadrao");
    }
  }, [dataForm, errorForm, history]);

  // Carregar item
  const {
    request: textoPadraoRequest,
    data: textoPadraoData,
    errors: textoPadraoErrors,
    isLoading: textoPadraoIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      textoPadraoRequest(apiRoutes.fiscalizacao.parametrizacao.consultaTextoPadrao, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [textoPadraoRequest, state]);

  useEffect(() => {
    if ((!textoPadraoData && !textoPadraoErrors) || textoPadraoIsLoading) return;

    if (!formik.values.sequenceTextoPadrao && textoPadraoData && textoPadraoData.result.length) {
      const info = {
        codigo: textoPadraoData?.result[0]?.sequenceTextoPadrao,
        data: {
          documentoInclusao: textoPadraoData?.result[0]?.usuarioInclusao,
          nomeFormalInclusao: textoPadraoData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: textoPadraoData?.result[0]?.nomeFormalAlteracao,
          documentoAlteracao: textoPadraoData?.result[0]?.usuarioAlteracao,
          dataInclusao: textoPadraoData?.result[0]?.dataInclusao,
          dataAlteracao: textoPadraoData?.result[0]?.dataAlteracao,
        },
      };

      setDadosInfo(info);

      formik.setValues({ ...textoPadraoData.result[0] });
    }

    if (textoPadraoErrors) {
      toast.error(textoPadraoErrors.message);
    }
  }, [formik, textoPadraoData, textoPadraoErrors, textoPadraoIsLoading]);

  const tipoDocumentoChangeHandler = (e) => {
    if (e.target.value === "P") {
      formik.setFieldValue("sequenceTipoAcaoFiscal", "");
      formik.setFieldValue("descricaoTipoAcaoFiscal", "");
      formik.setFieldValue("fgContabil", false);
    }
  };

  const editorRef = useRef(null);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Texto OS/SPF</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.sequenceTextoPadrao ? "Alteração do Texto OS/SPF" : "Inclusão do Texto OS/SPF"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="sequenceTextoPadrao" />
                </Col>
              </Row>

              {/* <Button href="#" className="btn btn-primary btn-sm btn-user">Botão</Button> */}
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={dataForm}
                errors={errorForm}
                isLoading={isLoading || textoPadraoIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.sequenceTextoPadrao
                      ? !isAllowed(funcionalidades.textoPadrao.alterar)
                      : !isAllowed(funcionalidades.textoPadrao.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col} md={4} sm={2}>
                      <Form.Label>Tipo de Documento</Form.Label>
                      <br />
                      <RadioButtonFormik
                        inline="inline"
                        formik={formik}
                        field="tipoDocumento"
                        label="Ordem de Serviço"
                        value="O"
                        onChange={tipoDocumentoChangeHandler}
                      />
                      <RadioButtonFormik
                        inline="inline"
                        formik={formik}
                        field="tipoDocumento"
                        label="Procedimento Fiscal"
                        value="P"
                        onChange={tipoDocumentoChangeHandler}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={6} sm={2}>
                      <Form.Label>Tipo de Ação Fiscal</Form.Label>
                      <br />
                      <SelectTipoAcaoFiscalFormik
                        formik={formik}
                        field="sequenceTipoAcaoFiscal"
                        disabled={formik.values.tipoDocumento === "P"}
                      />
                      {formik.values.descricaoTipoAcaoFiscal?.toUpperCase() === "AUDITORIA" && (
                        <CheckBoxFormik
                          inline="inline"
                          type="checkbox"
                          formik={formik}
                          field="fgContabil"
                          label="Fisco-contábil"
                          style={{ marginTop: 7 }}
                        />
                      )}
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} md={12} sm={2}>
                      <Form.Label>Texto Padrão</Form.Label>
                      <HtmlEditor formik={formik} field="textoPadrao" rows="4" ref={editorRef} />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Texto Padrão..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.sequenceTextoPadrao
                            ? funcionalidades.textoPadrao.alterar
                            : funcionalidades.textoPadrao.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      />
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.sequenceTextoPadrao && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormTextoPadrao;
